import { create } from 'zustand';

type FeatureFlagsIdentifiedStore = {
  /** Whether LaunchDarkly has been identified with the logged in user. */
  areFeatureFlagsIdentified: boolean;
  setAreFeatureFlagsIdentified: (identified: boolean) => void;
};

/**
 * LaunchDarkly initializes against an anonymous context and only later gets
 * identified by `FeatureFlagProvider`, so `useFeatureFlags` briefly returns
 * values that were not evaluated for the logged in user.
 *
 * This store records when that window is over, so callers that take
 * irreversible action on a flag value (e.g. reloading the app) do not act on
 * the anonymous values and then flip back once the real ones arrive.
 */
export const useFeatureFlagsIdentifiedStore =
  create<FeatureFlagsIdentifiedStore>((set) => ({
    areFeatureFlagsIdentified: false,
    setAreFeatureFlagsIdentified: (identified) =>
      set({ areFeatureFlagsIdentified: identified }),
  }));

/**
 * Whether feature flag values are evaluated for the logged in user.
 *
 * @returns True once LaunchDarkly has been identified.
 */
export const useAreFeatureFlagsIdentified = (): boolean =>
  useFeatureFlagsIdentifiedStore((state) => state.areFeatureFlagsIdentified);
